import * as React from 'react';
import * as Metadata from '../../../../entities/Metadata';
import { EntityType } from '../../../../entities/common';
import { Resource } from '../../../../store/ResourcesListStore';
import { IInputProps } from '../../interfaces/IInputProps';
import IntegerFieldInputElement from '../IntegerFieldInputElement';
import { ActionButton } from 'office-ui-fabric-react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { CostRateTable } from '../../../../store/CostRateTableStore';
import { resolveTimephasedFieldFromStore } from '../../../../services/TimephasedConfigService';

import './TimephasedValueInput.css';
import { getEffectiveTimephasedValueFromResource, getTimephasedInlineEditState } from '../../../../services/TimephasedValueService';
import { IValidator } from '../../../../validation';
import { getDefaultCostRateTable } from '../../../../store/selectors/costRateTableSelectors';
import { DisplayFieldService } from '../../DisplayFieldService';
import { isLockedField } from '../../../utils/common';
import { getTimephasedConfig } from '../../../resource/TimephasedDataPanel/TimephasedDataPanel';
import TimephasedDataPanel from '../../../resource/TimephasedDataPanel';
import { UserState } from '../../../../store/User';
import { TenantState } from '../../../../store/Tenant';

interface OwnProps extends IInputProps {
    field: Metadata.Field;
    entity: Resource;
    inlineOnly?: boolean;
}

interface StateProps {
    resolvedField: Metadata.Field;
    defaultCostRateTable?: CostRateTable;
    validator?: IValidator;
    isFieldLocked: boolean;
    user: UserState;
    tenant: TenantState;
}

export type TimephasedValueInputProps = OwnProps & StateProps;

function formatShortDate(d: Date): string {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const TimephasedValueInput: React.FC<TimephasedValueInputProps> = (props) => {
    const { field, entity, readOnly, disabled, defaultCostRateTable, inlineOnly, resolvedField, validator, onErrorRender, isFieldLocked, user, tenant } = props;
    const now = React.useMemo(() => new Date(), []);
    const [isTimephasedPanelOpen, setIsTimephasedPanelOpen] = React.useState(false);

    const effectiveReadOnly = readOnly || isFieldLocked;
    const effectiveDisabled = disabled || isFieldLocked;

    const inlineState = React.useMemo(
        () => getTimephasedInlineEditState(field.name, entity, defaultCostRateTable?.id, effectiveReadOnly, effectiveDisabled),
        [field.name, entity, defaultCostRateTable?.id, effectiveReadOnly, effectiveDisabled]
    );

    const effective = React.useMemo(
        () => getEffectiveTimephasedValueFromResource(field.name, entity, now, defaultCostRateTable?.id),
        [field, entity, now, defaultCostRateTable?.id]
    );
    const effectiveValue = effective.value;
    const effectiveUntil = effective.effectiveUntil;
    const panelHintTitle = (inlineState.shouldShowPanelHint && !isFieldLocked)
        ? `${field.label} field has custom periods defined. To make changes, edit it in the ${getTimephasedConfig(field.name)?.title} panel${inlineOnly ? ' from the Resource Dashboard' : ''}.`
        : undefined;

    const handleOpenPanel = React.useCallback(() => {
        setIsTimephasedPanelOpen(true);
    }, []);

    const handleClosePanel = React.useCallback(() => {
        setIsTimephasedPanelOpen(false);
    }, []);

    return (
        <div className="timephased-value-input" title={panelHintTitle}>
            <IntegerFieldInputElement
                {...props}
                field={resolvedField}
                entityType={EntityType.Resource}
                value={effectiveValue}
                inlineEditing={inlineOnly}
                validator={inlineState.isInlineEditable ? validator : undefined}
                onErrorRender={onErrorRender}
                disabled={!inlineState.isInlineEditable}
                readOnly={!inlineState.isInlineEditable}
                user={user}
                tenant={tenant}
            />

            {!isFieldLocked && effectiveUntil && (
                <div className="timephased-value-input__until">Effective until {formatShortDate(effectiveUntil)}</div>
            )}
            {!inlineOnly && !readOnly && !disabled && !isFieldLocked && (
                <ActionButton
                    className="timephased-value-input__edit"
                    iconProps={{ iconName: 'Edit' }}
                    onClick={(e) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                        handleOpenPanel();
                    }}
                    title="Edit timephased data"
                    styles={{
                        root: {
                            position: 'absolute',
                            right: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            minWidth: 24,
                            height: 24,
                            padding: 0,
                            backgroundColor: 'transparent'
                        },
                        icon: {
                            color: '#128b10',
                            fontSize: 16
                        }
                    }}
                />
            )}
            {isTimephasedPanelOpen && (
                <TimephasedDataPanel
                    isOpen={isTimephasedPanelOpen}
                    onDismiss={handleClosePanel}
                    resource={entity}
                    field={field}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps): StateProps => {
    const resolvedField = resolveTimephasedFieldFromStore(state.fields, ownProps.field) ?? ownProps.field;
    return {
        resolvedField,
        defaultCostRateTable: getDefaultCostRateTable(state),
        validator: DisplayFieldService.buildValidatorFromField(resolvedField),
        isFieldLocked: isLockedField(resolvedField.settings?.format, resolvedField.name, state.user, state.tenant),
        user: state.user,
        tenant: state.tenant
    };
};

export default connect(mapStateToProps)(TimephasedValueInput);
